import React from 'react';
import { 
  Heart,
  Home,
  MapPin,
  Calendar,
  Users,
  Sparkles,
  Wine,
  Camera,
  Plane,
  Car,
  Utensils,
  Music,
  CheckCircle2,
  Flower2,
  Mail,
  Phone
} from 'lucide-react';

const DestinationWeddingTemplate = ({ quote, onPayment, isAgentPreview = false }) => {
  if (!quote) {
    return null;
  }

  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Helper function to format dates elegantly
  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Parse date in local timezone to avoid UTC offset issues
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get couple names from title
  const getCoupleNames = () => {
    if (quote.quoteTitle) {
      return quote.quoteTitle;
    }
    if (quote.client?.firstName && quote.client?.lastName) {
      return `${quote.client.firstName} & Partner`;
    }
    return 'Your Destination Wedding';
  };

  // Categorize components
  const categorizeComponents = () => {
    const venue = [];
    const celebration = [];
    const logistics = [];

    quote.components?.forEach(component => {
      const type = component.type?.toLowerCase() || '';
      const title = component.title?.toLowerCase() || '';

      if (type.includes('hotel') || type.includes('resort') || title.includes('resort') || title.includes('hotel')) {
        venue.push(component);
      } else if (type.includes('flight') || type.includes('car') || type.includes('transfer') || title.includes('flight') || title.includes('transfer')) {
        logistics.push(component);
      } else {
        celebration.push(component);
      }
    });

    return { venue, celebration, logistics };
  };

  const { venue, celebration, logistics } = categorizeComponents();

  // Get celebration icon
  const getCelebrationIcon = (component) => {
    const type = component.type?.toLowerCase() || '';
    const title = component.title?.toLowerCase() || '';

    if (title.includes('ceremony') || title.includes('wedding')) {
      return <Heart className="w-6 h-6 text-rose-600" />;
    } else if (title.includes('reception') || title.includes('party') || title.includes('dinner')) {
      return <Wine className="w-6 h-6 text-rose-600" />;
    } else if (title.includes('photo') || title.includes('photographer')) {
      return <Camera className="w-6 h-6 text-rose-600" />;
    } else if (type.includes('dining') || type.includes('restaurant')) {
      return <Utensils className="w-6 h-6 text-rose-600" />;
    } else if (title.includes('music') || title.includes('entertainment')) {
      return <Music className="w-6 h-6 text-rose-600" />;
    }
    return <Sparkles className="w-6 h-6 text-rose-600" />;
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Romantic Hero Section */}
      <div className="relative min-h-[40vh] flex items-center justify-center bg-gradient-to-br from-rose-100 via-stone-100 to-amber-50">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/20 to-white/40"></div>
        
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <Heart className="w-16 h-16 text-rose-600 mx-auto mb-8 animate-pulse" />
          
          <h1 className="font-serif text-5xl md:text-7xl text-stone-800 mb-6 tracking-wide leading-tight">
            {getCoupleNames()}
          </h1>
          
          <div className="flex items-center justify-center gap-4 text-stone-600">
            <div className="w-16 h-px bg-rose-300"></div>
            <span className="font-serif text-xl tracking-widest">Destination Wedding Proposal</span>
            <div className="w-16 h-px bg-rose-300"></div>
          </div>

          {quote.travelDates?.start && (
            <p className="mt-8 text-stone-600 text-lg font-light tracking-wide">
              {formatDate(quote.travelDates.start)}
              {quote.travelDates.end && ` - ${formatDate(quote.travelDates.end)}`}
            </p>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-16">
        
        {/* Part 1: The Venue/Resort */}
        {venue.length > 0 && (
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="font-serif text-4xl text-stone-800 mb-3 tracking-wide">Your Wedding Venue</h2>
              <p className="text-stone-600 font-light">Where your celebration begins</p>
              <div className="w-24 h-px bg-rose-300 mx-auto mt-4"></div>
            </div>

            {venue.map((component, index) => (
              <div
                key={component.id || index}
                className="bg-white rounded-2xl shadow-xl overflow-hidden border-2 border-rose-100"
              >
                <div className="bg-gradient-to-r from-rose-50 to-amber-50 p-10 border-b-2 border-rose-100">
                  <div className="flex items-center justify-center gap-4 mb-6">
                    <Home className="w-10 h-10 text-rose-600" />
                    <h3 className="font-serif text-4xl text-stone-800 tracking-wide">
                      {component.title || 'Wedding Resort'}
                    </h3>
                  </div>
                  
                  {component.data?.location && (
                    <div className="flex items-center justify-center gap-2 text-stone-600">
                      <MapPin className="w-5 h-5 text-rose-600" />
                      <span className="text-lg">{component.data.location}</span>
                    </div>
                  )}
                </div>

                <div className="p-10">
                  {/* Room Category & Details */}
                  {component.data && Object.keys(component.data).length > 0 && (
                    <div className="grid md:grid-cols-2 gap-8 mb-8">
                      {Object.entries(component.data).map(([key, value]) => {
                        if (!value || key === 'isOption' || key === 'optionDescription') return null;
                        
                        const isPriceField = key.toLowerCase().includes('price') || 
                                             key.toLowerCase().includes('cost') || 
                                             key.toLowerCase().includes('fee') ||
                                             key.toLowerCase().includes('total') ||
                                             key.toLowerCase().includes('deposit');
                        const isNumeric = !isNaN(parseFloat(value)) && isFinite(value);
                        
                        let displayValue = String(value);
                        if (isPriceField && isNumeric) {
                          displayValue = formatCurrency(parseFloat(value));
                        }
                        
                        return (
                          <div key={key} className="bg-rose-50/50 rounded-lg p-6 border border-rose-100">
                            <div className="text-xs uppercase tracking-widest text-rose-600 mb-2 font-semibold">
                              {key.replace(/_/g, ' ')}
                            </div>
                            <div className="text-stone-800 text-lg font-semibold">
                              {displayValue}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Venue Investment */}
                  <div className="pt-8 border-t-2 border-rose-100 flex justify-between items-center">
                    <span className="text-stone-600 font-serif text-xl">Venue Investment</span>
                    <span className="font-serif text-3xl text-stone-800">{formatCurrency(component.price)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Part 2: The Celebration Itinerary */}
        {celebration.length > 0 && (
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="font-serif text-4xl text-stone-800 mb-3 tracking-wide">Celebration Itinerary</h2>
              <p className="text-stone-600 font-light">Your special moments, beautifully planned</p>
              <div className="w-24 h-px bg-rose-300 mx-auto mt-4"></div>
            </div>

            <div className="space-y-6">
              {celebration.map((component, index) => (
                <div
                  key={component.id || index}
                  className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-rose-200 hover:shadow-xl transition-shadow"
                >
                  <div className="p-8">
                    <div className="flex items-start justify-between gap-6">
                      <div className="flex items-start gap-6 flex-1">
                        <div className="mt-1">
                          {getCelebrationIcon(component)}
                        </div>
                        
                        <div className="flex-1">
                          <div className="mb-3">
                            <span className="inline-block px-4 py-1 bg-rose-100 text-rose-700 text-xs uppercase tracking-widest rounded-full font-semibold">
                              {component.type || 'Event'}
                            </span>
                          </div>
                          
                          <h3 className="font-serif text-2xl text-stone-800 mb-4 tracking-wide">
                            {component.title || `Event ${index + 1}`}
                          </h3>

                          {/* Event Details */}
                          {component.data && Object.keys(component.data).length > 0 && (
                            <div className="grid md:grid-cols-2 gap-4">
                              {Object.entries(component.data).map(([key, value]) => {
                                if (!value || key === 'isOption' || key === 'optionDescription') return null;
                                
                                const isPriceField = key.toLowerCase().includes('price') || 
                                                     key.toLowerCase().includes('cost') || 
                                                     key.toLowerCase().includes('fee') ||
                                                     key.toLowerCase().includes('total') ||
                                                     key.toLowerCase().includes('deposit');
                                const isNumeric = !isNaN(parseFloat(value)) && isFinite(value);
                                
                                let displayValue = String(value);
                                if (isPriceField && isNumeric) {
                                  displayValue = formatCurrency(parseFloat(value));
                                }
                                
                                return (
                                  <div key={key} className="flex items-start gap-2">
                                    <Sparkles className="w-4 h-4 text-rose-400 mt-1 flex-shrink-0" />
                                    <div>
                                      <div className="text-xs text-stone-500 uppercase tracking-wide">
                                        {key.replace(/_/g, ' ')}
                                      </div>
                                      <div className="text-stone-800 font-medium">
                                        {displayValue}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Component Photos */}
                          {component.photos && component.photos.length > 0 && (
                            <div className="mt-6">
                              <h4 className="text-sm font-serif font-bold text-rose-900 mb-3 flex items-center gap-2">
                                <span>📸</span> Photos
                              </h4>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                {component.photos.map((photo, photoIndex) => (
                                  <div 
                                    key={photoIndex} 
                                    className="relative group cursor-pointer rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow border-2 border-rose-200 bg-white"
                                    onClick={() => window.open(photo.url, '_blank')}
                                  >
                                    <img 
                                      src={photo.url} 
                                      alt={photo.name || 'Component photo'} 
                                      className="w-full h-auto object-contain"
                                      style={{ maxHeight: '300px' }}
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-rose-900/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2">
                                      <span className="text-white text-sm font-semibold">{photo.name || 'View Photo'}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Component Documents */}
                          {component.documents && component.documents.length > 0 && (
                            <div className="mt-6">
                              <h4 className="text-sm font-serif font-bold text-rose-900 mb-3 flex items-center gap-2">
                                <span>📋</span> Documents
                              </h4>
                              <div className="space-y-2">
                                {component.documents.map((doc, docIndex) => (
                                  <a 
                                    key={docIndex}
                                    href={doc.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-3 bg-rose-50 hover:bg-rose-100 px-4 py-2 rounded-2xl transition-colors border border-rose-200"
                                  >
                                    <span className="text-lg">📄</span>
                                    <span className="text-rose-900 text-sm font-semibold">{doc.name}</span>
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="text-right flex-shrink-0">
                        <div className="text-xs text-stone-500 uppercase tracking-widest mb-2">Investment</div>
                        <div className="font-serif text-2xl text-stone-800">
                          {formatCurrency(component.price)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Part 3: Travel Logistics */}
        {logistics.length > 0 && (
          <div className="mb-20">
            <div className="text-center mb-10">
              <h3 className="text-2xl font-semibold text-stone-700 mb-2">Travel Logistics</h3>
              <p className="text-stone-500 text-sm">Getting you there and back</p>
            </div>

            <div className="space-y-4">
              {logistics.map((component, index) => (
                <div
                  key={component.id || index}
                  className="bg-white rounded-lg border border-stone-200 p-6 flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    {component.type?.toLowerCase().includes('flight') ? (
                      <Plane className="w-5 h-5 text-stone-500" />
                    ) : (
                      <Car className="w-5 h-5 text-stone-500" />
                    )}
                    <div>
                      <div className="font-semibold text-stone-800">{component.title}</div>
                      <div className="text-sm text-stone-500">{component.type}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-stone-800">{formatCurrency(component.price)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Special Notes */}
        {quote.notes && (
          <div className="mb-20 bg-gradient-to-br from-rose-50 to-amber-50 rounded-xl border-2 border-rose-100 p-10">
            <div className="flex items-center gap-3 mb-6">
              <Heart className="w-6 h-6 text-rose-600" />
              <h3 className="font-serif text-2xl text-stone-800">Special Notes</h3>
            </div>
            <p className="text-stone-700 leading-relaxed whitespace-pre-wrap font-light">
              {quote.notes}
            </p>
          </div>
        )}

        {/* Pricing Summary - Elegant */}
        <div className="bg-gradient-to-br from-white via-rose-50/30 to-amber-50/30 rounded-2xl shadow-2xl overflow-hidden border-2 border-rose-200">
          <div className="p-12 text-center">
            <div className="flex items-center justify-center gap-4 mb-8">
              <div className="w-20 h-px bg-rose-300"></div>
              <Heart className="w-8 h-8 text-rose-600" />
              <div className="w-20 h-px bg-rose-300"></div>
            </div>

            <h3 className="font-serif text-3xl text-stone-800 mb-8 tracking-wide">Your Wedding Investment</h3>

            <div className="bg-white/60 backdrop-blur-sm rounded-xl p-8 mb-8 border border-rose-100">
              <div className="text-stone-600 text-sm uppercase tracking-widest mb-3">Total Investment</div>
              <div className="font-serif text-6xl text-stone-800 mb-2">
                {formatCurrency(quote.totalPrice)}
              </div>
              <p className="text-stone-500 font-light">For your dream destination wedding</p>
            </div>

            {quote.depositAmount > 0 && (
              <div className="grid md:grid-cols-2 gap-6 mb-10">
                <div className="bg-rose-50/50 rounded-xl p-6 border border-rose-100">
                  <div className="text-rose-600 text-xs uppercase tracking-widest mb-2 font-semibold">Initial Deposit</div>
                  <div className="font-serif text-3xl text-stone-800">{formatCurrency(quote.depositAmount)}</div>
                </div>
                {quote.depositDueDate && (
                  <div className="bg-rose-50/50 rounded-xl p-6 border border-rose-100">
                    <div className="text-rose-600 text-xs uppercase tracking-widest mb-2 font-semibold">Due Date</div>
                    <div className="text-2xl text-stone-800 font-light">{formatDate(quote.depositDueDate)}</div>
                  </div>
                )}
              </div>
            )}

            {/* Payment Button - Rose Gold Style */}
            {onPayment && (
              <div>
                <button
                  onClick={onPayment}
                  disabled={isAgentPreview}
                  className={`w-full bg-gradient-to-r from-rose-400 via-rose-500 to-amber-400 hover:from-rose-500 hover:via-rose-600 hover:to-amber-500 text-white font-serif py-6 px-8 rounded-xl text-xl tracking-wide transition-all duration-300 flex items-center justify-center gap-3 ${
                    isAgentPreview ? 'opacity-50 cursor-not-allowed' : 'shadow-xl hover:shadow-2xl hover:scale-105'
                  }`}
                >
                  <CheckCircle2 className="w-6 h-6" />
                  {isAgentPreview ? 'Preview Mode' : 'Approve Proposal'}
                  <Heart className="w-6 h-6" />
                </button>
                {!isAgentPreview && (
                  <p className="text-stone-500 text-sm mt-6 font-light">
                    Begin your journey to forever
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Agent Contact Footer */}
        {(quote.agent_name || quote.agent_email || quote.agent_phone) && (
          <div className="mt-12 bg-gradient-to-br from-rose-50 via-pink-50 to-rose-50 rounded-3xl border-2 border-rose-200 overflow-hidden shadow-xl">
            <div className="bg-gradient-to-r from-rose-500 to-pink-500 px-6 py-5">
              <div className="flex items-center justify-center gap-3 text-white">
                <Heart className="w-6 h-6" />
                <h3 className="text-lg font-serif font-bold">Your Wedding Specialist</h3>
                <Heart className="w-6 h-6" />
              </div>
            </div>
            
            <div className="p-6">
              {quote.agent_name && (
                <div className="text-center mb-4">
                  <p className="text-xl font-serif font-bold text-rose-900">{quote.agent_name}</p>
                  {quote.agent_business_name && (
                    <p className="text-rose-700 font-semibold">{quote.agent_business_name}</p>
                  )}
                </div>
              )}
              
              <div className="space-y-3 max-w-md mx-auto">
                {quote.agent_email && (
                  <a 
                    href={`mailto:${quote.agent_email}`}
                    className="flex items-center gap-3 bg-white px-4 py-3 rounded-2xl hover:bg-rose-50 transition-colors border border-rose-200 shadow-sm"
                  >
                    <Mail className="w-5 h-5 text-rose-600" />
                    <span className="text-rose-900 font-medium">{quote.agent_email}</span>
                  </a>
                )}
                
                {quote.agent_phone && (
                  <a 
                    href={`tel:${quote.agent_phone}`}
                    className="flex items-center gap-3 bg-white px-4 py-3 rounded-2xl hover:bg-rose-50 transition-colors border border-rose-200 shadow-sm"
                  >
                    <Phone className="w-5 h-5 text-rose-600" />
                    <span className="text-rose-900 font-medium">{quote.agent_phone}</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-16 text-center">
          <div className="flex items-center justify-center gap-4 text-stone-400">
            <div className="w-16 h-px bg-rose-200"></div>
            <Heart className="w-5 h-5" />
            <span className="font-serif text-sm tracking-widest">With Love</span>
            <Heart className="w-5 h-5" />
            <div className="w-16 h-px bg-rose-200"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DestinationWeddingTemplate;
